// src/core-pages/ReviewDashboard.js

import Footer from '../components/core-components/Footer';
import LibraryNavigation from '../components/team-library/LibraryNavigation';
import LinkedRecordEntry from '../components/team-library/LinkedRecordEntry';

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MapFolderList from '../components/Review/MapFolderList';
import VideoList from '../components/Review/VideoList';
import PovReviewPlayer from '../components/Review/PovReviewPlayer';
import SummaryFocusSidebar from '../components/Review/ImprovementNotesSidebar';

import { useAuth } from '../context/AuthContext';
import { useTeam, TeamProvider } from '../context/TeamContext';

import UploadVideoModal from '../components/Review/UploadVideoModal';
import GeneralImprovementNotesTop from '../components/Review/GeneralImprovementNotesTop';
import LiveLobbyPanel from '../components/Review/LiveLobbyPanel';
import LiveLobbyRoomModal from '../components/Review/LiveLobbyRoomModal';
import CustomAlert from '../components/core-components/CustomAlert';
import useTeamQuotaStatus from '../hooks/useTeamQuotaStatus';

import LiveRecordModal from '../components/Review/LiveRecordModal';
import ReviewHelpMenu from "../components/Review/ReviewHelpMenu";

import ReviewOverviewPanel from "../components/Review/ReviewOverviewPanel";
import { joinLiveLobby } from "../utils/liveLobby";

import Select from 'react-select';

import LoginTeamGate from "../components/core-components/LoginTeamGate";

import './ReviewDashboard.css';
import '../components/Review/A-Temp.css';

// ✅ Add these for map-name fallback fetch:
import { db } from '../Security/firebase-config';
import { doc, getDoc } from 'firebase/firestore';

export default function ReviewDashboard(){const location=useLocation(),requested=new URLSearchParams(location.search).get("teamId")||new URLSearchParams(location.search).get("team");return requested?<TeamProvider selectedTeamId={requested}><ReviewDashboardContent/></TeamProvider>:<ReviewDashboardContent/>;}
function ReviewDashboardContent() {
  const { mapId: paramMap, videoId: paramVid } = useParams();
  const navigate = useNavigate(), location=useLocation();

  // ───────────────────────────────────────────────────────────────
  // 1) Initialize from sessionStorage (or URL params)
  // ───────────────────────────────────────────────────────────────
  const [selectedMapId, setSelectedMapId] = useState(() => {
    return paramMap || sessionStorage.getItem('review:selectedMapId') || null;
  });

  const [selectedMapName, setSelectedMapName] = useState(() => {
    return sessionStorage.getItem('review:selectedMapName') || '';
  });

  const [selectedVideoId, setSelectedVideoId] = useState(() => {
    return paramVid || sessionStorage.getItem('review:selectedVideoId') || null;
  });

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [showLiveRecordModal, setShowLiveRecordModal] = useState(false);
  const [liveLobbySession, setLiveLobbySession] = useState(null);


  const [sortMethod, setSortMethod] = useState('date');
  const [videoRefreshToggle, setVideoRefreshToggle] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [filterMode, setFilterMode] = useState('OR');
  const [hideReviewedByMe, setHideReviewedByMe] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const filterRef = useRef();

  const [alertMsg, setAlertMsg] = useState('');
  const { userData, loading: authLoading } = useAuth();
  const { teamId, loading: teamLoading } = useTeam();
  const handledLibraryAction=useRef('');
  useEffect(()=>{
    const params=new URLSearchParams(location.search),action=params.get('libraryAction');
    if(!action||teamLoading||!teamId||handledLibraryAction.current===location.search)return;
    handledLibraryAction.current=location.search;
    if(action==='import')setShowUploadModal(true);else if(action==='record')setShowLiveRecordModal(true);
  },[location.search,teamLoading,teamId]);

  // ───────────────────────────────────────────────────────────────
  // 1.5) Keep selectedMapId / selectedVideoId synced with URL params
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedMapId(paramMap || null);
    if (!paramMap) {
      setSelectedMapName('');
    }
  }, [paramMap]);

  useEffect(() => {
    setSelectedVideoId(paramVid || null);
  }, [paramVid]);

  // ✅ Fallback: if user deep-links and mapName isn't known, fetch it
  useEffect(() => {
    let cancelled = false;

    async function hydrateMapNameIfMissing() {
      if (!teamId || !selectedMapId) return;

      // If we already have a name, don't overwrite it
      if (selectedMapName && selectedMapName.trim().length > 0) return;

      try {
        const mapRef = doc(db, 'teams', teamId, 'maps', selectedMapId);
        const snap = await getDoc(mapRef);

        if (cancelled) return;

        if (snap.exists()) {
          // ✅ Adjust this field name if your doc uses something else:
          const nameFromDb = snap.data()?.name || snap.data()?.mapName || '';
          if (nameFromDb) {
            setSelectedMapName(nameFromDb);
          }
        }
      } catch (err) {
        // Non-fatal: we'll just show blank name if fetch fails
        console.warn('Failed to hydrate map name:', err);
      }
    }

    hydrateMapNameIfMissing();

    return () => {
      cancelled = true;
    };
  }, [teamId, selectedMapId, selectedMapName]);

  // ───────────────────────────────────────────────────────────────
  // 2) Persist selections in sessionStorage
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedMapId) {
      sessionStorage.setItem('review:selectedMapId', selectedMapId);
    } else {
      sessionStorage.removeItem('review:selectedMapId');
    }
  }, [selectedMapId]);

  useEffect(() => {
    if (selectedMapName) {
      sessionStorage.setItem('review:selectedMapName', selectedMapName);
    } else {
      sessionStorage.removeItem('review:selectedMapName');
    }
  }, [selectedMapName]);

  useEffect(() => {
    if (selectedVideoId) {
      sessionStorage.setItem('review:selectedVideoId', selectedVideoId);
    } else {
      sessionStorage.removeItem('review:selectedVideoId');
    }
  }, [selectedVideoId]);

  // ───────────────────────────────────────────────────────────────
  // 3) Quota
  // ───────────────────────────────────────────────────────────────
  const quotaStatus = useTeamQuotaStatus(teamId);
  const quotaExceeded = quotaStatus.uploadBlocked;

  // ───────────────────────────────────────────────────────────────
  // 4) Handle clicks outside filter panel
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  // ───────────────────────────────────────────────────────────────
  // 5) Selecting map / selecting video
  // ───────────────────────────────────────────────────────────────
  const handleMapSelect = (id, name) => {
    setSelectedMapId(id);
    setSelectedMapName(name);
    setSelectedVideoId(null);
    setShowFilters(false);
    navigate(`/review/${id}`);
  };

  const handleVideoSelect = (vid) => {
    setSelectedVideoId(vid);
    navigate(`/review/${selectedMapId}/${vid}`);
  };

  const handleJoinLiveLobby = async (lobby) => {
    if (!teamId || !lobby?.id) return;

    try {
      const response = await joinLiveLobby({
        teamId,
        lobbyId: lobby.id,
      });

      if (!response?.ok) {
        throw new Error("Unable to join this live lobby.");
      }

      setLiveLobbySession({
        ...response,
        hostMode: false,
      });
    } catch (error) {
      console.error("Failed to join live lobby:", error);
      setAlertMsg(
        error?.message ||
          "Unable to join this live lobby right now."
      );
    }
  };

  // ───────────────────────────────────────────────────────────────
  // 6) Styles for react-select
  // ───────────────────────────────────────────────────────────────
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'var(--ReviewDash-filter-select-bg-clr)',
      borderColor: 'var(--ReviewDash-filter-select-border-clr)',
      color: 'var(--ReviewDash-filter-select-clr)',
      fontWeight: 600,
      borderRadius: '0.4rem',
      boxShadow: state.isFocused
        ? `0 0 0 1px var(--ReviewDash-filter-select-border-clr)`
        : 'none',
      '&:hover': {
        borderColor: 'var(--ReviewDash-filter-select-border-clr)',
      },
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: 'var(--ReviewDash-filter-panel-bg-clr)',
      zIndex: 1001,
    }),

    menuList: (base) => ({
      ...base,
      backgroundColor: 'var(--ReviewDash-filter-panel-bg-clr)',
    }),

    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? 'var(--ReviewDash-filter-select-option-selected-bg-clr)'
        : isFocused
          ? 'var(--ReviewDash-filter-select-option-hover-bg-clr)'
          : 'var(--ReviewDash-filter-select-bg-clr)',
      color: isSelected
        ? 'var(--ReviewDash-filter-select-option-selected-clr)'
        : 'var(--ReviewDash-filter-select-clr)',
      fontWeight: isSelected ? 700 : 500,
      cursor: 'pointer',
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--ReviewDash-filter-select-option-selected-bg-clr)',
      border: `1px solid var(--ReviewDash-filter-select-border-clr)`,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: 'var(--ReviewDash-filter-select-clr)',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'var(--ReviewDash-filter-select-clr)',
      ':hover': {
        backgroundColor: 'var(--ReviewDash-filter-select-option-hover-bg-clr)',
        color: 'var(--ReviewDash-filter-select-clr)',
      },
    }),
  };

  // ───────────────────────────────────────────────────────────────
  // 7) Restriction states
  // ───────────────────────────────────────────────────────────────
  if (authLoading || teamLoading) {
    return <LoginTeamGate />;
  }

  if (!userData) {
    return <LoginTeamGate />;
  }

  if (!teamId) {
    return <LoginTeamGate />;
  }

  return (
    <>
      <LinkedRecordEntry teamId={teamId}><div className="review-dashboard"><LibraryNavigation/>
        {alertMsg && (
          <CustomAlert
            message={alertMsg}
            type="error"
            position="top"
            onClose={() => setAlertMsg('')}
          />
        )}

        <LiveLobbyPanel onJoinLobby={handleJoinLiveLobby} />

        {!selectedMapId && (
          <div className="review-folder-and-overview">
            <div className="review-folder-and-overview__left">
              <MapFolderList onSelect={handleMapSelect} />
            </div>

            <div className="review-folder-and-overview__right">
              <div className="review-folder-and-overview__overview">
                <ReviewOverviewPanel
                  onOpenVideo={(mapId, mapName, videoId) => {
                    // Jump straight into that video
                    setSelectedMapId(mapId);
                    setSelectedMapName(mapName);
                    setSelectedVideoId(videoId);
                    navigate(`/review/${mapId}/${videoId}`);
                  }}
                />
              </div>
              <div className="review-folder-and-overview__general-notes">
                <GeneralImprovementNotesTop />
              </div>
            </div>
          </div>
        )}

        {selectedMapId && !selectedVideoId && (
          <>
            <div className="map-header">
              <button onClick={() => navigate('/review')}>⬅ Back to Folders</button>
              <h3>{selectedMapName}</h3>

              <div className="map-header-actions">
                <div className="filter-wrapper">
                  <button onClick={() => setShowFilters((v) => !v)}>Filter 🔍</button>

                  {showFilters && (
                    <div className="filter-panel" ref={filterRef}>
                      <div className="filter-group">
                        <label htmlFor="sort-select">Sort by:</label>
                        <select
                          id="sort-select"
                          value={sortMethod}
                          onChange={(e) => setSortMethod(e.target.value)}
                        >
                          <option value="date">Upload Date</option>
                          <option value="title">Alphabetical</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label htmlFor="filter-mode">Match:</label>
                        <select
                          id="filter-mode"
                          value={filterMode}
                          onChange={(e) => setFilterMode(e.target.value)}
                        >
                          <option value="OR">Any (OR)</option>
                          <option value="AND">All (AND)</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label htmlFor="combined-filter">Filter by:</label>
                        <Select
                          inputId="combined-filter"
                          isMulti
                          styles={customSelectStyles}
                          options={[
                            { value: '__NOT_REVIEWED__', label: 'Not Reviewed by Me' },
                            ...availableTags.map((tag) => ({ value: tag, label: tag })),
                          ]}
                          value={[
                            ...(hideReviewedByMe
                              ? [{ value: '__NOT_REVIEWED__', label: 'Not Reviewed by Me' }]
                              : []),
                            ...selectedTags.map((tag) => ({ value: tag, label: tag })),
                          ]}
                          onChange={(selectedOptions) => {
                            const values = (selectedOptions || []).map((opt) => opt.value);
                            setHideReviewedByMe(values.includes('__NOT_REVIEWED__'));
                            setSelectedTags(values.filter((v) => v !== '__NOT_REVIEWED__'));
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className={quotaExceeded ? 'quota-exceeded' : ''}
                  onClick={() => {
                    if (quotaExceeded) {
                      setAlertMsg(quotaStatus.uploadBlockMessage || 'Uploading is unavailable right now.');
                    } else {
                      setShowUploadModal(true);
                    }
                  }}
                  disabled={quotaExceeded}
                >
                  + Upload VOD
                </button>

                <button
                  className={quotaExceeded ? 'quota-exceeded' : ''}
                  onClick={() => {
                    if (quotaExceeded) {
                      setAlertMsg(quotaStatus.uploadBlockMessage || 'Uploading is unavailable right now.');
                    }
                    else setShowLiveRecordModal(true);
                  }}
                  disabled={quotaExceeded}
                >
                  + Record
                </button>
                <ReviewHelpMenu />
              </div>
            </div>

            <LiveLobbyPanel
              mapId={selectedMapId}
              title={`${selectedMapName || "This Map"} Live Lobbies`}
              onJoinLobby={handleJoinLiveLobby}
            />


            <div className="video-and-sidebar-wrapper">
              <VideoList
                mapId={selectedMapId}
                onSelectVideo={handleVideoSelect}
                refreshTrigger={videoRefreshToggle}
                sortMethod={sortMethod}
                selectedTags={selectedTags}
                filterMode={filterMode}
                hideReviewedByMe={hideReviewedByMe}
                setAvailableTags={setAvailableTags}
              />
              <div className="ReviewDashboard-Summary-Focus-sidebar">
                <SummaryFocusSidebar mapId={selectedMapId} />
              </div>
            </div>
          </>
        )}

        {selectedMapId && selectedVideoId && (
          <PovReviewPlayer
            mapId={selectedMapId}
            mapName={selectedMapName}
            initialVideoId={selectedVideoId}
            onBackToMaps={() => navigate('/review')}
            onBackToVideos={() => navigate(`/review/${selectedMapId}`)}
          />
        )}

        {showUploadModal && (
          <UploadVideoModal
            mapId={selectedMapId}
            mapName={selectedMapName}
            onClose={() => setShowUploadModal(false)}
            onVideoUploaded={() => setVideoRefreshToggle((prev) => !prev)}
          />
        )}

        {showLiveRecordModal && (
          <LiveRecordModal
            mapId={selectedMapId}
            mapName={selectedMapName}
            onClose={() => setShowLiveRecordModal(false)}
            onVideoUploaded={() => setVideoRefreshToggle((prev) => !prev)}
          />
        )}

        {liveLobbySession && (
          <LiveLobbyRoomModal
            session={liveLobbySession}
            hostMode={false}
            onClose={() => setLiveLobbySession(null)}
            onLobbyFinalized={() => {
              setVideoRefreshToggle((prev) => !prev);
            }}
          />
        )}

      </div>

      </LinkedRecordEntry><Footer />
    </>
  );
}
